import { useMemo, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { Users, Plus, Pencil, Trash2, Loader2, Crown, Clock } from "lucide-react";
import { customFetch } from "@workspace/api-client-react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { WhatsAppInput } from "@/components/ui/whatsapp-input";
import { ModalShell } from "@/components/ui/modal-shell";
import { useConfirm } from "@/components/ui/confirm-dialog";
import { useToast } from "@/hooks/use-toast";
import { useAuth } from "@/hooks/use-auth";
import { useFeatureAllowed } from "@/hooks/use-plan";
import { UltraBadge } from "@/components/UltraBadge";
import { SectionErrorBoundary } from "@/components/ErrorBoundary";
import { extractApiErrorMessage } from "@/lib/api-error";
import { ADMIN_PAGES, readAccess } from "@/lib/access";

/**
 * Configurações → Equipe: quem acessa esta loja e com quais páginas.
 *
 * Só aparece pra quem tem acesso total (dono / conta sem restrição). A lista
 * de páginas é a mesma do menu, filtrada pelo tipo da loja e pelo opt-in do
 * Caixa — caixa de seleção de página que a loja nem tem seria promessa vazia.
 */

interface Person {
  kind: "owner" | "full" | "member";
  id: string;
  userId: string | null;
  name: string;
  whatsappFormattedNumber: string;
  pages: string[] | null;
  pending: boolean;
  isMe: boolean;
}
interface MembersResp {
  canManage: boolean;
  people: Person[];
}

const QK = ["settings", "members"] as const;

export function EquipeSection({ cashRegisterEnabled }: { cashRegisterEnabled: boolean }) {
  const { user } = useAuth();
  const access = readAccess(user);
  if (access.pages !== null) return null;
  return (
    <SectionErrorBoundary anchorId="cfg-equipe" label="Equipe">
      <EquipeCard storeType={(user as any)?.store?.storeType || "produtos"} cashRegisterEnabled={cashRegisterEnabled} />
    </SectionErrorBoundary>
  );
}

function EquipeCard({ storeType, cashRegisterEnabled }: { storeType: string; cashRegisterEnabled: boolean }) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const confirm = useConfirm();
  const [editing, setEditing] = useState<Person | "new" | null>(null);

  const { data, isLoading } = useQuery({
    queryKey: QK,
    queryFn: () => customFetch<MembersResp>("/api/settings/members"),
  });

  const pagesForStore = useMemo(
    () =>
      ADMIN_PAGES.filter((p) => {
        if (p.optIn === "cashRegister" && !cashRegisterEnabled) return false;
        return p.showFor.includes(storeType);
      }),
    [storeType, cashRegisterEnabled],
  );
  const labelOf = (key: string) => ADMIN_PAGES.find((p) => p.key === key)?.label ?? key;

  const remove = useMutation({
    mutationFn: (id: string) => customFetch(`/api/settings/members/${id}`, { method: "DELETE" }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: QK });
      toast({ title: "Acesso removido." });
    },
    onError: (err) => toast({ title: extractApiErrorMessage(err, "Não foi possível remover."), variant: "destructive" }),
  });

  const people = data?.people ?? [];
  const canManage = !!data?.canManage;
  // Plano Ultra OU grant da loja (whitelist do Pedro) — o servidor decide igual.
  const ultra = useFeatureAllowed("team");

  return (
    <Card className="p-6 space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-2">
        <div className="flex items-center gap-2 text-lg font-semibold"><Users className="w-5 h-5 text-primary" /> Equipe</div>
        {canManage && (ultra ? (
          <Button type="button" size="sm" variant="outline" onClick={() => setEditing("new")}>
            <Plus className="w-4 h-4 mr-1" /> Adicionar pessoa
          </Button>
        ) : (
          // Quem já foi adicionado fica (editar/remover continuam); só entrar
          // gente nova é do Ultra.
          <UltraBadge />
        ))}
      </div>
      <p className="text-xs text-muted-foreground -mt-2">
        Quem acessa esta loja. A pessoa entra com o próprio WhatsApp, pelo mesmo login de sempre.
      </p>

      {isLoading ? (
        <div className="flex items-center gap-2 text-sm text-muted-foreground"><Loader2 className="w-4 h-4 animate-spin" /> Carregando…</div>
      ) : (
        <div className="divide-y rounded-md border">
          {people.map((p) => (
            <div key={`${p.kind}-${p.id}`} className="flex items-center justify-between gap-3 px-3 py-2.5">
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="text-sm font-medium truncate">{p.name || p.whatsappFormattedNumber}</span>
                  {p.isMe && <span className="text-[11px] text-muted-foreground">(você)</span>}
                </div>
                <div className="text-xs text-muted-foreground truncate">{p.whatsappFormattedNumber}</div>
                <div className="mt-1 flex flex-wrap gap-1">
                  {p.kind === "owner" && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 text-primary px-2 py-0.5 text-[11px] font-medium"><Crown className="w-3 h-3" /> Dono</span>
                  )}
                  {p.kind === "full" && (
                    <span className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[11px] font-medium">Acesso total</span>
                  )}
                  {p.kind === "member" && p.pending && (
                    <span className="inline-flex items-center gap-1 rounded-full bg-amber-50 text-amber-700 px-2 py-0.5 text-[11px] font-medium"><Clock className="w-3 h-3" /> Aguardando primeiro acesso</span>
                  )}
                  {p.kind === "member" &&
                    (p.pages ?? []).filter((k) => k !== "dashboard").map((k) => (
                      <span key={k} className="inline-flex items-center rounded-full bg-muted px-2 py-0.5 text-[11px]">{labelOf(k)}</span>
                    ))}
                </div>
              </div>
              {canManage && p.kind === "member" && (
                <div className="flex items-center gap-1 shrink-0">
                  <Button type="button" size="sm" variant="ghost" onClick={() => setEditing(p)} aria-label="Editar acessos">
                    <Pencil className="w-4 h-4" />
                  </Button>
                  <Button
                    type="button"
                    size="sm"
                    variant="ghost"
                    className="text-red-600"
                    aria-label="Remover"
                    disabled={remove.isPending}
                    onClick={async () => {
                      if (
                        await confirm({
                          title: "Remover acesso?",
                          description: `${p.name || p.whatsappFormattedNumber} deixa de entrar nesta loja agora.`,
                          confirmLabel: "Remover",
                          destructive: true,
                        })
                      ) {
                        remove.mutate(p.id);
                      }
                    }}
                  >
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
          ))}
          {people.length === 0 && <div className="px-3 py-3 text-sm text-muted-foreground">Ninguém ainda.</div>}
        </div>
      )}

      {editing && (
        <PersonModal
          person={editing === "new" ? null : editing}
          pagesForStore={pagesForStore}
          onClose={() => setEditing(null)}
        />
      )}
    </Card>
  );
}

function PersonModal({
  person,
  pagesForStore,
  onClose,
}: {
  person: Person | null;
  pagesForStore: typeof ADMIN_PAGES;
  onClose: () => void;
}) {
  const qc = useQueryClient();
  const { toast } = useToast();
  const [number, setNumber] = useState("");
  const [ddi, setDdi] = useState("55");
  // Configurações começa desmarcada de propósito: é onde ficam plano,
  // pagamentos e o WhatsApp de login.
  const [pages, setPages] = useState<Set<string>>(
    () => new Set(person?.pages ?? pagesForStore.filter((p) => p.key !== "configuracoes").map((p) => p.key)),
  );
  const toggle = (key: string, on: boolean) =>
    setPages((prev) => {
      const next = new Set(prev);
      if (on) next.add(key); else next.delete(key);
      return next;
    });

  const save = useMutation({
    mutationFn: async () => {
      const body = { pages: Array.from(pages) };
      if (person) {
        return customFetch(`/api/settings/members/${person.id}`, { method: "PATCH", body: JSON.stringify(body) });
      }
      return customFetch<{ notified: boolean; alreadyHasAccount: boolean }>("/api/settings/members", {
        method: "POST",
        body: JSON.stringify({ whatsappNumber: number, ddi, ...body }),
      });
    },
    onSuccess: (res: any) => {
      qc.invalidateQueries({ queryKey: QK });
      toast({
        title: person ? "Acessos atualizados." : "Pessoa adicionada.",
        description: person
          ? undefined
          : res?.notified
            ? "Ela recebeu um aviso no WhatsApp com o link de acesso."
            : "Ela entra pelo login normal com o próprio número.",
      });
      onClose();
    },
    onError: (err) => toast({ title: extractApiErrorMessage(err, "Não foi possível salvar."), variant: "destructive" }),
  });

  const canSave = person ? true : number.replace(/\D/g, "").length >= 8;

  return (
    <ModalShell
      open
      onOpenChange={(o) => { if (!o) onClose(); }}
      title={person ? "Editar acessos" : "Adicionar pessoa"}
      size="sm"
      footer={
        <div className="flex justify-end gap-2">
          <Button type="button" variant="outline" onClick={onClose} disabled={save.isPending}>Cancelar</Button>
          <Button type="button" onClick={() => save.mutate()} disabled={!canSave || save.isPending}>
            {save.isPending && <Loader2 className="w-4 h-4 mr-1 animate-spin" />}
            {person ? "Salvar" : "Adicionar"}
          </Button>
        </div>
      }
    >
      <div className="space-y-4">
        {person ? (
          <div className="text-sm">
            <div className="font-medium">{person.name || person.whatsappFormattedNumber}</div>
            <div className="text-xs text-muted-foreground">{person.whatsappFormattedNumber}</div>
          </div>
        ) : (
          <div className="space-y-1">
            <Label className="text-sm">WhatsApp da pessoa</Label>
            <WhatsAppInput id="equipe-whatsapp" value={number} onChange={setNumber} ddi={ddi} onDdiChange={setDdi} placeholder="(00) 00000-0000" />
          </div>
        )}
        <div className="space-y-2">
          <Label className="text-sm">Páginas que ela pode abrir</Label>
          <div className="grid grid-cols-2 gap-2">
            {pagesForStore.map((p) => (
              <label key={p.key} className={`flex items-center gap-2 rounded-md border px-3 py-2 text-sm ${p.fixed ? "opacity-60" : "cursor-pointer"}`}>
                <Checkbox
                  checked={p.fixed || pages.has(p.key)}
                  disabled={!!p.fixed}
                  onCheckedChange={(v) => toggle(p.key, v === true)}
                />
                <span>{p.label}</span>
              </label>
            ))}
          </div>
        </div>
      </div>
    </ModalShell>
  );
}
